import { z } from 'zod';
import type { AmazonItemDeal } from '../types/deals';

const amazonItemDealSchema = z.object({
  asin: z.string(),
  title: z.string().optional(),
  image: z.string().optional(),
  price: z.string().optional(),
  priceAsOf: z.number(),
  dealClickBase: z.string().min(1),
  deal: z.object({
    state: z.enum(['deal', 'prime', 'savings']),
    badge: z.string().optional(),
    startTime: z.string().optional(),
    endTime: z.string().optional(),
    savingsPercent: z.number().optional(),
    savingBasisLabel: z.string().optional(),
    basisAmount: z.number().optional(),
    price: z.number().optional(),
    ccy: z.string().optional(),
  }),
});

function parseAmazonItemDeal(raw: unknown): AmazonItemDeal | null {
  const parsed = amazonItemDealSchema.safeParse(raw);
  return parsed.success ? (parsed.data as AmazonItemDeal) : null;
}

export const getAmazonItemDeals = async (): Promise<AmazonItemDeal[]> => {
  const res = await fetch('/api/deals/amazon', { cache: 'no-store' });
  if (!res.ok) throw new Error(`Error fetching Amazon item deals: HTTP ${res.status}`);
  const data: unknown = await res.json();
  if (!Array.isArray(data)) return [];
  return data.flatMap((raw) => parseAmazonItemDeal(raw) ?? []);
};

export const getAmazonItemDeal = async (asin: string): Promise<AmazonItemDeal | null> => {
  const res = await fetch(`/api/deals/amazon/${encodeURIComponent(asin)}`, { cache: 'no-store' });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Error fetching Amazon item deal: HTTP ${res.status}`);
  return parseAmazonItemDeal(await res.json());
};
